/**
 * SHIPPING ESTIMATOR
 *
 * Calcula o frete da variante atual da página do produto.
 *
 * Fluxo:
 *
 * 1. Salva o carrinho atual.
 * 2. Identifica a variante selecionada.
 * 3. Adiciona temporariamente 1 unidade da variante ao carrinho.
 * 4. Consulta o frete para o CEP.
 * 5. Mostra os resultados.
 * 6. Restaura o carrinho original.
 */

if (!customElements.get('shipping-estimator')) {

  class ShippingEstimator extends HTMLElement {

    constructor() {

      super();

      this.estimateShipping =
        this.estimateShipping.bind(this);

      this.isCalculating = false;
    }


    connectedCallback() {

      this.setupCountries();

      this.submitButton =
        this.querySelector(
          '.thb-embedded-btn'
        );

      this.results =
        this.querySelector(
          '.shipping-estimator--results'
        );


      if (!this.submitButton) {

        console.error(
          'Shipping Estimator: botão não encontrado.'
        );

        return;
      }


      this.submitButton.addEventListener(
        'click',
        this.estimateShipping
      );
    }


    /*
     * ========================================================
     * PAÍS / ESTADO
     * ========================================================
     */

    setupCountries() {

      const countrySelect =
        this.querySelector(
          '.shipping-estimator-country'
        );


      if (
        typeof Shopify !== 'undefined' &&
        Shopify.CountryProvinceSelector
      ) {

        const formId =
          this.dataset.formId;


        new Shopify.CountryProvinceSelector(

          `shipping-estimator-country-${formId}`,

          `shipping-estimator-province-${formId}`,

          {
            hideElement:
              `shipping-estimator-province-wrapper-${formId}`
          }

        );
      }


      /*
       * Força Brasil.
       *
       * country_option_tags da Shopify usa o nome
       * do país como value.
       */

      if (countrySelect) {

        const brazil =
          Array.from(
            countrySelect.options
          ).find(option => {

            const text =
              option.textContent
                .trim()
                .toLowerCase();

            const value =
              option.value
                .trim()
                .toLowerCase();


            return (
              value === 'brazil' ||
              text === 'brazil' ||
              text === 'brasil'
            );

          });


        if (brazil) {

          countrySelect.value =
            brazil.value;


          countrySelect.dispatchEvent(
            new Event(
              'change',
              {
                bubbles: true
              }
            )
          );
        }
      }
    }


    /*
     * ========================================================
     * IDENTIFICAR VARIANTE
     * ========================================================
     */

    getVariantId() {

      /*
       * Primeiro tenta encontrar o formulário
       * principal de produto.
       */

      const productForms =
        Array.from(
          document.querySelectorAll(
            'form[action*="/cart/add"]'
          )
        );


      for (
        const form of productForms
      ) {

        const variantInput =
          form.querySelector(
            '[name="id"]'
          );


        if (
          variantInput &&
          variantInput.value
        ) {

          const id =
            parseInt(
              variantInput.value,
              10
            );


          if (
            Number.isFinite(id) &&
            id > 0
          ) {

            return id;
          }
        }
      }


      /*
       * Tenta qualquer [name="id"].
       */

      const anyVariantInput =
        document.querySelector(
          '[name="id"]'
        );


      if (
        anyVariantInput &&
        anyVariantInput.value
      ) {

        const id =
          parseInt(
            anyVariantInput.value,
            10
          );


        if (
          Number.isFinite(id) &&
          id > 0
        ) {

          return id;
        }
      }


      /*
       * Último fallback:
       * variante enviada pelo Liquid.
       */

      const fallback =
        parseInt(
          this.dataset.defaultVariantId,
          10
        );


      if (
        Number.isFinite(fallback) &&
        fallback > 0
      ) {

        return fallback;
      }


      return null;
    }


    /*
     * ========================================================
     * ENDEREÇO
     * ========================================================
     */

    getShippingAddress() {

      const zipInput =
        this.querySelector(
          '.shipping-estimator-zip'
        );


      const countryInput =
        this.querySelector(
          '.shipping-estimator-country'
        );


      const provinceInput =
        this.querySelector(
          '.shipping-estimator-province'
        );


      /*
       * CEP
       */

      const zip =
        zipInput
          ? zipInput.value
              .trim()
              .replace(/\D/g, '')
          : '';


      /*
       * País
       */

      let country = 'Brazil';


      if (
        countryInput &&
        countryInput.selectedIndex >= 0
      ) {

        const selected =
          countryInput.options[
            countryInput.selectedIndex
          ];


        if (selected) {

          country =
            selected.textContent.trim();


          if (
            country.toLowerCase() ===
            'brasil'
          ) {

            country = 'Brazil';
          }
        }
      }


      /*
       * Estado
       */

      let province = '';


      if (
        provinceInput &&
        provinceInput.selectedIndex >= 0
      ) {

        const selected =
          provinceInput.options[
            provinceInput.selectedIndex
          ];


        if (
          selected &&
          selected.value
        ) {

          province =
            selected.textContent.trim();
        }
      }


      return {
        zip,
        country,
        province
      };
    }


    /*
     * ========================================================
     * CALCULAR
     * ========================================================
     */

    async estimateShipping() {

      if (this.isCalculating) {
        return;
      }


      this.isCalculating = true;


      this.submitButton.classList.add(
        'loading'
      );


      this.results.innerHTML = '';


      let originalCart = null;


      try {

        /*
         * ----------------------------------------------------
         * CEP
         * ----------------------------------------------------
         */

        const address =
          this.getShippingAddress();


        if (
          !address.zip ||
          address.zip.length !== 8
        ) {

          throw new Error(
            'Digite um CEP válido com 8 números.'
          );
        }


        /*
         * ----------------------------------------------------
         * VARIANTE
         * ----------------------------------------------------
         */

        const variantId =
          this.getVariantId();


        if (!variantId) {

          throw new Error(
            'Não foi possível identificar a variante deste produto.'
          );
        }


        console.log(
          'SHIPPING ESTIMATOR',
          {
            variantId,
            address
          }
        );


        /*
         * ----------------------------------------------------
         * SALVA O CARRINHO ATUAL
         * ----------------------------------------------------
         */

        originalCart =
          await this.getCart();


        /*
         * ----------------------------------------------------
         * ADICIONA TEMPORARIAMENTE O PRODUTO
         * ----------------------------------------------------
         */

        await this.addTemporaryProduct(
          variantId
        );


        /*
         * ----------------------------------------------------
         * CALCULA O FRETE
         * ----------------------------------------------------
         */

        const rates =
          await this.calculateRates(
            address
          );


        /*
         * ----------------------------------------------------
         * MOSTRA RESULTADOS
         * ----------------------------------------------------
         */

        this.formatShippingRates(
          rates
        );


      } catch (error) {

        console.error(
          'Shipping Estimator:',
          error
        );


        this.formatError(
          error.message ||
          'Não foi possível calcular o frete.'
        );


      } finally {

        /*
         * ----------------------------------------------------
         * RESTAURA O CARRINHO
         * ----------------------------------------------------
         */

        if (originalCart) {

          try {

            await this.restoreCart(
              originalCart
            );

          } catch (restoreError) {

            console.error(
              'Erro ao restaurar carrinho:',
              restoreError
            );
          }
        }


        this.submitButton.classList.remove(
          'loading'
        );


        this.isCalculating = false;
      }
    }


    /*
     * ========================================================
     * GET CART
     * ========================================================
     */

    async getCart() {

      const response =
        await fetch(
          window.Shopify.routes.root +
          'cart.js',
          {
            method: 'GET',

            headers: {
              'Accept':
                'application/json'
            },

            cache: 'no-store'
          }
        );


      if (!response.ok) {

        throw new Error(
          'Não foi possível acessar o carrinho.'
        );
      }


      return response.json();
    }


    /*
     * ========================================================
     * ADD TEMPORARY PRODUCT
     * ========================================================
     */

    async addTemporaryProduct(
      variantId
    ) {

      const response =
        await fetch(
          window.Shopify.routes.root +
          'cart/add.js',
          {

            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              'Accept':
                'application/json'
            },

            body: JSON.stringify({

              items: [

                {
                  id: variantId,

                  quantity: 1
                }

              ]

            })
          }
        );


      const data =
        await response.json();


      if (!response.ok) {

        console.error(
          'Erro ao adicionar variante temporária:',
          data
        );


        throw new Error(
          data.description ||
          data.message ||
          'Não foi possível preparar o produto para calcular o frete.'
        );
      }


      return data;
    }


    /*
     * ========================================================
     * CALCULAR SHIPPING
     * ========================================================
     */

    async calculateRates(
      address
    ) {

      const params =
        new URLSearchParams();


      params.set(
        'shipping_address[zip]',
        address.zip
      );


      params.set(
        'shipping_address[country]',
        address.country
      );


      if (
        address.province
      ) {

        params.set(
          'shipping_address[province]',
          address.province
        );
      }


      /*
       * IMPORTANTE:
       *
       * O produto já está temporariamente
       * no carrinho neste momento.
       */

      const prepareUrl =
        window.Shopify.routes.root +
        'cart/prepare_shipping_rates.json?' +
        params.toString();


      console.log(
        'PREPARE SHIPPING:',
        prepareUrl
      );


      const prepareResponse =
        await fetch(
          prepareUrl,
          {
            method: 'POST',

            headers: {
              'Accept':
                'application/json'
            }
          }
        );


      /*
       * Shopify normalmente retorna 202.
       */

      if (
        !prepareResponse.ok
      ) {

        const errorText =
          await prepareResponse.text();


        console.error(
          'PREPARE SHIPPING ERROR:',
          prepareResponse.status,
          errorText
        );


        throw new Error(
          `Erro ao preparar o frete (${prepareResponse.status}).`
        );
      }


      /*
       * Agora aguardamos o cálculo.
       */

      return this.pollShippingRates(
        params
      );
    }


    /*
     * ========================================================
     * POLLING
     * ========================================================
     */

    async pollShippingRates(
      params,
      attempt = 0
    ) {

      if (
        attempt >= 20
      ) {

        throw new Error(
          'A Shopify demorou muito para retornar as opções de frete.'
        );
      }


      const url =
        window.Shopify.routes.root +
        'cart/async_shipping_rates.json?' +
        params.toString();


      const response =
        await fetch(
          url,
          {
            method: 'GET',

            headers: {
              'Accept':
                'application/json'
            },

            cache: 'no-store'
          }
        );


      if (
        !response.ok
      ) {

        const errorText =
          await response.text();


        console.error(
          'ASYNC SHIPPING ERROR:',
          response.status,
          errorText
        );


        throw new Error(
          `Erro ao consultar taxas (${response.status}).`
        );
      }


      const text =
        await response.text();


      /*
       * Ainda calculando.
       */

      if (
        !text ||
        text.trim() === 'null'
      ) {

        await this.sleep(
          500
        );


        return this.pollShippingRates(
          params,
          attempt + 1
        );
      }


      let data;


      try {

        data =
          JSON.parse(text);

      } catch (error) {

        console.error(
          'Resposta inválida da Shopify:',
          text
        );


        throw new Error(
          'A Shopify retornou uma resposta inválida ao calcular o frete.'
        );
      }


      console.log(
        'SHIPPING RATES:',
        data
      );


      return (
        data.shipping_rates ||
        []
      );
    }


    /*
     * ========================================================
     * RESTAURAR CARRINHO
     * ========================================================
     */

    async restoreCart(
      originalCart
    ) {

      /*
       * Pega o estado atual.
       *
       * Isso permite descobrir qual linha foi
       * adicionada temporariamente.
       */

      const currentCart =
        await this.getCart();


      const originalKeys =
        new Set(
          originalCart.items.map(
            item => item.key
          )
        );


      /*
       * Montamos um objeto de updates.
       *
       * Primeiro restauramos as quantidades
       * originais.
       */

      const updates = {};


      originalCart.items.forEach(
        item => {

          updates[item.key] =
            item.quantity;
        }
      );


      /*
       * Tudo que não existia originalmente
       * foi criado pelo cálculo.
       *
       * Portanto removemos.
       */

      currentCart.items.forEach(
        item => {

          if (
            !originalKeys.has(
              item.key
            )
          ) {

            updates[item.key] = 0;
          }
        }
      );


      /*
       * Se não houve alteração,
       * não precisa chamar API.
       */

      if (
        Object.keys(updates).length === 0
      ) {

        return;
      }


      const response =
        await fetch(
          window.Shopify.routes.root +
          'cart/update.js',
          {

            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              'Accept':
                'application/json'
            },

            body: JSON.stringify({
              updates
            })
          }
        );


      if (
        !response.ok
      ) {

        const text =
          await response.text();


        throw new Error(
          `Não foi possível restaurar o carrinho: ${text}`
        );
      }


      console.log(
        'Carrinho restaurado.'
      );
    }


    /*
     * ========================================================
     * RESULTADOS
     * ========================================================
     */

    formatShippingRates(
      shippingRates
    ) {

      if (
        !shippingRates ||
        shippingRates.length === 0
      ) {

        this.results.innerHTML = `
          <p>
            Nenhuma opção de frete disponível
            para este CEP.
          </p>
        `;

        return;
      }


      const format =
        window.theme &&
        window.theme.settings &&
        window.theme.settings
          .money_with_currency_format
          ?
          window.theme.settings
            .money_with_currency_format
          :
          'R$ {{amount}}';


      let html = '';


      shippingRates.forEach(
        rate => {

          const price =
            parseFloat(
              rate.price
            ) * 100;


          let formattedPrice;


          if (
            typeof formatMoney ===
            'function'
          ) {

            formattedPrice =
              formatMoney(
                price,
                format
              );

          } else {

            formattedPrice =
              this.formatFallbackMoney(
                price
              );
          }


          html += `

            <li>

              <span>
                ${
                  rate.presentment_name ||
                  rate.name ||
                  'Frete'
                }
              </span>

              <strong>
                ${formattedPrice}
              </strong>

            </li>

          `;
        }
      );


      const title =
        shippingRates.length === 1
          ?
          'Opção de envio disponível:'
          :
          'Opções de envio disponíveis:';


      this.results.innerHTML = `

        <p>
          ${title}
        </p>

        <ul class="shipping-estimator-rates">
          ${html}
        </ul>

      `;
    }


    /*
     * ========================================================
     * ERRO
     * ========================================================
     */

    formatError(
      message
    ) {

      this.results.innerHTML = `

        <p>
          ${this.escapeHtml(message)}
        </p>

      `;
    }


    /*
     * ========================================================
     * UTILITÁRIOS
     * ========================================================
     */

    sleep(
      milliseconds
    ) {

      return new Promise(
        resolve =>
          setTimeout(
            resolve,
            milliseconds
          )
      );
    }


    formatFallbackMoney(
      cents
    ) {

      return (
        'R$ ' +
        (
          cents / 100
        )
        .toFixed(2)
        .replace('.', ',')
      );
    }


    escapeHtml(
      text
    ) {

      const div =
        document.createElement(
          'div'
        );


      div.textContent =
        text;


      return div.innerHTML;
    }
  }


  customElements.define(
    'shipping-estimator',
    ShippingEstimator
  );
}
